import { useI18nStore } from "@/stores/i18nStore";
import { VitalSignsChartData } from "@/types/fhir-vitals";
import {
  formatChartDate,
  formatTooltipTimestamp,
} from "@/utils/dateLocalization";
import React from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

interface VitalSignsChartProps {
  data: VitalSignsChartData[];
}

type VitalSignsChartPoint = VitalSignsChartData & { fhirTimestamp?: string };

const CustomTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ payload: VitalSignsChartPoint }>;
}> = ({ active, payload }) => {
  const { t } = useI18nStore();
  if (active && payload && payload.length) {
    const data = payload[0]?.payload;
    if (!data) return null;
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="text-sm font-medium text-gray-900">
          {data.formattedDate}
        </p>
        <p className="text-xs text-gray-500 mb-2">
          {t.recorded}: {data.fhirTimestamp ?? data.timestamp}
        </p>
        <div className="mt-2 space-y-1">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-red-600 rounded-full"></div>
            <span className="text-sm text-gray-600">
              {t.systolic}: {data.systolic} {t.mmHg}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-blue-600 rounded-full"></div>
            <span className="text-sm text-gray-600">
              {t.diastolic}: {data.diastolic} {t.mmHg}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-600 rounded-full"></div>
            <span className="text-sm text-gray-600">
              {t.restingHeartRate}: {data.heartRate} {t.bpm}
            </span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

export const VitalSignsChart: React.FC<VitalSignsChartProps> = ({ data }) => {
  const { t, language } = useI18nStore();

  // ✅ FHIR Compliance: Sort by timestamp for chronological accuracy
  const sortedData = [...data].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );

  // Transform data with localized timestamps using date utilities
  const chartData = sortedData.map((item) => ({
    ...item,
    fhirTimestamp: formatTooltipTimestamp(item.timestamp, language),
    formattedDate: formatChartDate(item.timestamp, language),
  }));

  if (sortedData.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
        <div className="text-center text-gray-500">
          <div className="text-lg mb-2">📊</div>
          <p>{t.noVitalSignsData}</p>
          <p className="text-sm">{t.firstEntriesWillBeShown}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-64">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 8, left: 0, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="formattedDate" tick={{ fontSize: 12 }} />
          <YAxis tick={{ fontSize: 12 }} width={36} />
          <Tooltip content={<CustomTooltip />} />
          <Legend />

          {/* Reference lines for blood pressure */}
          <ReferenceLine y={120} stroke="#f59e0b" strokeDasharray="5 5" />
          <ReferenceLine y={140} stroke="#ef4444" strokeDasharray="5 5" />
          <ReferenceLine y={80} stroke="#22c55e" strokeDasharray="5 5" />

          {/* Blood pressure lines */}
          <Line
            type="monotone"
            dataKey="systolic"
            stroke="#dc2626"
            strokeWidth={2}
            dot={{ fill: "#dc2626", strokeWidth: 2, r: 4 }}
            name={t.systolicBloodPressure}
          />
          <Line
            type="monotone"
            dataKey="diastolic"
            stroke="#2563eb"
            strokeWidth={2}
            dot={{ fill: "#2563eb", strokeWidth: 2, r: 4 }}
            name={t.diastolicBloodPressure}
          />
          <Line
            type="monotone"
            dataKey="heartRate"
            stroke="#059669"
            strokeWidth={2}
            dot={{ fill: "#059669", strokeWidth: 2, r: 4 }}
            name={t.restingHeartRate}
          />
        </LineChart>
      </ResponsiveContainer>

      {/* Chart Summary - FHIR Data Points */}
      <div className="mt-4 flex justify-between items-center text-sm text-gray-600 px-2">
        <div className="flex items-center space-x-4">
          <span>
            📈 {chartData.length} {t.dataPoints}
          </span>
        </div>
      </div>
    </div>
  );
};
